import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { productsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { saveBlob } from '@/lib/download'
import Drawer from '@/components/common/Drawer'

/**
 * Import products from a CSV or Excel file.
 *
 * The rules are stated on the panel because they decide what the person
 * puts in the file: the item code is the key, a blank cell changes nothing,
 * and one bad row stops the whole file. The sample is offered here, beside
 * the upload, which is where somebody realises they need it.
 */
export default function ProductImport({ open, onClose, onDone }) {
  const queryClient = useQueryClient()
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  // Row problems from a refused file: [{ row, item_code, message }].
  const [rows, setRows] = useState(null)
  const [rowCount, setRowCount] = useState(0)
  const [downloading, setDownloading] = useState('')

  const reset = () => {
    setFile(null)
    setError('')
    setRows(null)
    setRowCount(0)
    if (inputRef.current) inputRef.current.value = ''
  }

  const close = () => {
    reset()
    onClose()
  }

  const upload = useMutation({
    mutationFn: () => productsApi.import(file),
    onSuccess: ({ created, updated }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['translation-pending'] })
      reset()
      onDone(
        `Import finished: ${created} product${created === 1 ? '' : 's'} added, ` +
          `${updated} updated.`,
      )
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail
      if (detail?.errors) {
        setError(detail.message)
        setRows(detail.errors)
        setRowCount(detail.error_count ?? detail.errors.length)
      } else {
        setError(errorMessage(e))
        setRows(null)
      }
    },
  })

  const sample = async (format) => {
    setDownloading(format)
    setError('')
    try {
      const { blob, filename } = await productsApi.sample(format)
      saveBlob(blob, filename)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setDownloading('')
    }
  }

  return (
    <Drawer
      open={open}
      size="wide"
      title="Import products"
      subtitle="Add new products and update existing ones from a CSV or Excel file."
      error={error}
      onErrorDismiss={() => setError('')}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!file || upload.isPending}
            onClick={() => {
              setError('')
              setRows(null)
              upload.mutate()
            }}
          >
            {upload.isPending ? 'Importing…' : 'Import'}
          </button>
        </>
      }
    >
      <section className="import-help">
        <ul>
          <li>
            <strong>Item code</strong> identifies the product. A code already in the catalog
            updates that product; a new code adds one.
          </li>
          <li>
            On an existing product, an <strong>empty cell leaves that field as it is</strong> —
            a file of just item codes and prices updates prices only.
          </li>
          <li>
            A new product needs at least an item name. Prices go in the tier columns
            A, A+, B, B+ and P.
          </li>
          <li>
            Every row is checked first. If any row has a problem,{' '}
            <strong>nothing is imported</strong> and each problem is listed by row.
          </li>
        </ul>

        <div className="import-sample">
          <span className="muted">Sample format:</span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={Boolean(downloading)}
            onClick={() => sample('xlsx')}
          >
            {downloading === 'xlsx' ? 'Preparing…' : 'Download Excel'}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={Boolean(downloading)}
            onClick={() => sample('csv')}
          >
            {downloading === 'csv' ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      </section>

      <label className="field">
        <span>File (.csv or .xlsx, up to 5 MB)</span>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null)
            setError('')
            setRows(null)
          }}
        />
      </label>

      {rows?.length > 0 && (
        <div className="import-errors">
          <table className="table compact">
            <thead>
              <tr>
                <th>Row</th>
                <th>Item code</th>
                <th>Problem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.row}-${i}`}>
                  <td>{r.row}</td>
                  <td className="mono">{r.item_code ?? '—'}</td>
                  <td>{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rowCount > rows.length && (
            <p className="hint">
              Showing the first {rows.length} of {rowCount} problems.
            </p>
          )}
        </div>
      )}
    </Drawer>
  )
}
